import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import { Add, Remove, CloudUpload, Save, Delete } from '@mui/icons-material';
import {
    Table,
    TableBody,
    TableHead,
    TableRow,
    Pagination,
    Box,
    Typography,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Divider,
    CircularProgress,
    Skeleton
} from '@mui/material';

import { useEnterpiseVisitRulesApi } from '@/services/enterpise-visits-rules';
import useVisitAbsencesService from '@/services/visits-absences';

import { AddVisitRuleModal } from '../../../../Common/EnterpriseDetails/Manage/tabs/Visits/components/AddVisitRuleModal';
import {
    ActionButton,
    BodyCell,
    HeaderCell,
    NumberInput,
    PaginationInfo,
    PaginationWrapper,
    PeriodButton,
    PerPageSelect,
    TableContainerStyled,
    TableStyled,
    TableHeaderActions
} from './styles';

import useActionsPaused from '@/hooks/useActionsPaused';
import useSnackbar from '@/hooks/useSnackbar';

const LoadingSkeleton = ({ rowsPerPage }) => (
    <>
        {Array.from({ length: rowsPerPage }).map((_, index) => (
            <TableRow key={index}>
                <BodyCell>
                    <Skeleton variant="text" width="80%" />
                </BodyCell>
                <BodyCell>
                    <Skeleton variant="text" width="80%" />
                </BodyCell>
                <BodyCell>
                    <Skeleton variant="text" width="80%" />
                </BodyCell>
                <BodyCell>
                    <Skeleton variant="rectangular" width={120} height={36} />
                </BodyCell>
                <BodyCell>
                    <Skeleton variant="rectangular" width={80} height={36} />
                </BodyCell>
            </TableRow>
        ))}
    </>
);

LoadingSkeleton.propTypes = {
    rowsPerPage: PropTypes.number.isRequired
};

export const VisitsAbsencesTable = ({ onDataChange, idEnterprise, absenceReasons = [], filterPer, collaborators, properties }) => {
    const actionsPaused = useActionsPaused();
    const { fetchVisitRules, updateVisitRule } = useEnterpiseVisitRulesApi();
    const { getAllPendingVisitAbsences, updateAbsenceReason } = useVisitAbsencesService();
    const { getRegularityStatus } = useVisitAbsencesService();
    const dispatch = useDispatch();
    const [page, setPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(15);
    const [isLoading, setIsLoading] = useState(true);
    const [userVisits, setUserVisits] = useState([]);
    const [loadingIds, setLoadingIds] = useState(new Set());
    const [paginationInfo, setPaginationInfo] = useState({
        total: 0,
        totalPages: 0,
        currentPage: 1,
        from: 0,
        to: 0
    });
    const [updatingIds, setUpdatingIds] = useState(new Set());
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [visitToDelete, setVisitToDelete] = useState(null);
    const snackbar = useSnackbar();

    // Memoiza os filtros processados para evitar recriação desnecessária
    const filters = useMemo(() => {
        const collaboratorIds = filterPer?.collaborators?.filter((c) => c.id !== null && c.id !== undefined).map((c) => c.id) || [];

        const propertyIds = filterPer?.properties?.filter((p) => p.id !== null && p.id !== undefined).map((p) => p.id) || [];

        // Mapeia o período de português para o formato da API
        let period = null;
        if (filterPer?.period) {
            const periodMap = {
                Meia: 'middle',
                Inteira: 'fulltime'
            };
            period = periodMap[filterPer.period] || null;
        }

        return { collaboratorIds, propertyIds, period };
    }, [filterPer?.collaborators, filterPer?.properties, filterPer?.period]);

    const loadData = async () => {
        setIsLoading(true);
        try {
            const params = {
                page,
                per_page: rowsPerPage
            };
            const response = await getAllPendingVisitAbsences(params);
            setUserVisits(response || []);

            // Update pagination info if available
            if (response.pagination) {
                setPaginationInfo(response.pagination);
            } else {
                // Set default pagination if not provided
                setPaginationInfo({
                    total: response?.length || 0,
                    totalPages: 1,
                    currentPage: page,
                    from: 0,
                    to: response?.length || 0
                });
            }
        } catch (error) {
            console.error('Erro ao buscar dados de ausências de visitas:', error);
            setUserVisits([]);
        } finally {
            setIsLoading(false);
        }
    };

    // Efeito para buscar dados quando página, limite, empresa ou filtros mudarem
    useEffect(() => {
        loadData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, rowsPerPage]);

    const handlePageChange = (_event, value) => {
        setPage(value);
    };

    const handleRowsPerPageChange = (event) => {
        setRowsPerPage(event.target.value);
        setPage(1); // Reset para primeira página ao mudar itens por página
    };

    // Função genérica para atualizar dados no servidor
    const updateDataOnServer = async (id, field, newValue) => {
        setUpdatingIds((prev) => new Set(prev).add(id));

        try {
            await updateVisitRule(id, { [field]: newValue });

            // Atualiza o estado local após sucesso
            setTableData((prevData) => prevData.map((row) => (row.id === id ? { ...row, [field]: newValue } : row)));

            // Notifica componente pai se houver callback
            onDataChange?.(tableData.map((row) => (row.id === id ? { ...row, [field]: newValue } : row)));
        } catch (error) {
            console.error('Erro ao atualizar visita:', error);
            // Em caso de erro, o estado local permanece inalterado
            // O usuário pode tentar novamente ou recarregar a página
        } finally {
            setUpdatingIds((prev) => {
                const newSet = new Set(prev);
                newSet.delete(id);
                return newSet;
            });
        }
    };

    const handleIncrement = async (id, field) => {
        const row = tableData.find((r) => r.id === id);
        if (!row) return;

        const newValue = row[field] + 1;

        // Validação: limite máximo de 100
        if (newValue > 100) {
            return;
        }

        // Validação: required_visits não pode ser maior que allowed_visits
        if (field === 'required_visits' && newValue > row.allowed_visits) {
            return;
        }

        await updateDataOnServer(id, field, newValue);
    };

    const handleDecrement = async (id, field) => {
        const row = tableData.find((r) => r.id === id);
        if (!row) return;

        // Define o mínimo baseado no campo
        const minValue = field === 'required_visits' ? 0 : 1;
        if (row[field] <= minValue) return;

        const newValue = Math.max(minValue, row[field] - 1);

        // Validação: allowed_visits não pode ser menor que required_visits
        if (field === 'allowed_visits' && newValue < row.required_visits) {
            return;
        }

        await updateDataOnServer(id, field, newValue);
    };

    const handleInputChange = async (id, field, value) => {
        const row = tableData.find((r) => r.id === id);
        if (!row) return;

        const numValue = parseInt(value, 10);

        // Define o mínimo baseado no campo
        const minValue = field === 'required_visits' ? 0 : 1;
        if (isNaN(numValue) || numValue < minValue || numValue > 100) return;

        // Validação: required_visits não pode ser maior que allowed_visits
        if (field === 'required_visits' && numValue > row.allowed_visits) {
            return;
        }

        // Validação: allowed_visits não pode ser menor que required_visits
        if (field === 'allowed_visits' && numValue < row.required_visits) {
            return;
        }

        await updateDataOnServer(id, field, numValue);
    };

    const handleAbsenceReasonChange = async (visitId, reasonId) => {
        // Update local state for immediate UI feedback
        setUserVisits((prevVisits) =>
            prevVisits.map((visit) => (visit.id === visitId ? { ...visit, visit_absence_reason_id: reasonId } : visit))
        );
    };

    const handleSaveAbsenceReason = async (visit) => {
        setLoadingIds((prev) => new Set(prev).add(visit.id));
        try {
            const data = {
                reason_id: visit.visit_absence_reason_id,
                status: visit.status
            };

            await updateAbsenceReason(visit.id, data);
            snackbar('Motivo de não visita atualizado com sucesso para fazenda: ' + visit.property.name, 'success');
            loadData();

            // Update regularity status after successful save
            try {
                const resp = await getRegularityStatus();
                dispatch({ type: 'SET_REGULARITY_STATUS', payload: resp.data });
            } catch (error) {
                console.log('Error updating regularity status:', error);
            }

            // Optionally show success message or refresh data
            // loadData();
        } catch (error) {
            snackbar('Falha ao atualizar o motivo da não visita: ' + error, 'error');
            // Optionally show error message
        } finally {
            setLoadingIds((prev) => {
                const newSet = new Set(prev);
                newSet.delete(visit.id);
                return newSet;
            });
        }
    };

    const handleDeleteAbsenceReason = (visit) => {
        setVisitToDelete(visit);
        setDeleteModalOpen(true);
    };

    const confirmDeleteAbsenceReason = async () => {
        if (!visitToDelete) return;

        setLoadingIds((prev) => new Set(prev).add(visitToDelete.id));
        try {
            const data = {
                reason_id: null,
                status: visitToDelete.status
            };

            await updateAbsenceReason(visitToDelete.id, data);
            snackbar('Motivo de não visita removido com sucesso para fazenda: ' + visitToDelete.property.name, 'success');
            loadData();

            // Update local state to reflect the change
            setUserVisits((prevVisits) => prevVisits.map((v) => (v.id === visitToDelete.id ? { ...v, visit_absence_reason_id: null } : v)));

            // Update regularity status after successful delete
            try {
                const resp = await getRegularityStatus();
                dispatch({ type: 'SET_REGULARITY_STATUS', payload: resp.data });
            } catch (error) {
                console.log('Error updating regularity status:', error);
            }
        } catch (error) {
            snackbar('Falha ao remover o motivo da não visita: ' + error, 'error');
            // Optionally show error message
        } finally {
            setLoadingIds((prev) => {
                const newSet = new Set(prev);
                newSet.delete(visitToDelete.id);
                return newSet;
            });
            setDeleteModalOpen(false);
            setVisitToDelete(null);
        }
    };

    const cancelDeleteAbsenceReason = () => {
        setDeleteModalOpen(false);
        setVisitToDelete(null);
    };

    const handleOpenModal = () => {
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
    };

    return (
        <Box mt={4} display="flex" flexDirection="column" flex={1}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6" component="h3" sx={{ fontWeight: 600, color: '#194727' }}>
                    Visitas não Realizadas
                </Typography>
                <TableHeaderActions></TableHeaderActions>
            </Box>

            <TableContainerStyled>
                <TableStyled>
                    <TableHead>
                        <TableRow>
                            <HeaderCell>Técnico</HeaderCell>
                            <HeaderCell>Fazenda</HeaderCell>
                            <HeaderCell>Fechamento do ciclo</HeaderCell>
                            <HeaderCell>Motivo não Visita</HeaderCell>
                            <HeaderCell>Ações</HeaderCell>
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {isLoading ? (
                            <LoadingSkeleton rowsPerPage={rowsPerPage} />
                        ) : userVisits.length === 0 ? (
                            <TableRow>
                                <BodyCell colSpan={5} align="center">
                                    <Typography variant="body2" color="textSecondary" py={4}>
                                        Nenhuma pendência encontrada
                                    </Typography>
                                </BodyCell>
                            </TableRow>
                        ) : (
                            userVisits.map((visit, index) => {
                                // console.log('Visit status:', visit.status, 'Visit:', visit);
                                return (
                                    <TableRow key={index} hover>
                                        <BodyCell>{visit.collaborator.name}</BodyCell>
                                        <BodyCell>{visit.property.name}</BodyCell>
                                        <BodyCell>
                                            <Typography
                                                sx={{
                                                    color: visit.status === 'late' ? '#d32f2f' : 'inherit'
                                                }}
                                            >
                                                {new Date(visit.closure_cycle.closing_date).toLocaleDateString('pt-BR')}
                                            </Typography>
                                        </BodyCell>
                                        <BodyCell>
                                            <FormControl size="small" fullWidth>
                                                <Select
                                                    value={visit.visit_absence_reason_id || ''}
                                                    onChange={(e) => handleAbsenceReasonChange(visit.id, e.target.value)}
                                                    displayEmpty
                                                >
                                                    <MenuItem value="">Selecione um motivo</MenuItem>
                                                    {absenceReasons.map((reason) => (
                                                        <MenuItem key={reason.id} value={reason.id}>
                                                            {reason.reason}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </BodyCell>
                                        <BodyCell>
                                            <Box display="flex" gap={1}>
                                                <Button
                                                    variant="contained"
                                                    size="small"
                                                    onClick={() => handleSaveAbsenceReason(visit)}
                                                    disabled={!visit.visit_absence_reason_id || loadingIds.has(visit.id) || actionsPaused}
                                                    sx={{
                                                        textTransform: 'none',
                                                        backgroundColor: '#3a6647',
                                                        fontSize: '0.875rem',
                                                        '&:hover': {
                                                            backgroundColor: '#2d5038'
                                                        },
                                                        '&.Mui-disabled': {
                                                            backgroundColor: '#ccc',
                                                            color: '#999'
                                                        }
                                                    }}
                                                >
                                                    Salvar
                                                </Button>
                                                <ActionButton
                                                    size="small"
                                                    onClick={() => handleDeleteAbsenceReason(visit)}
                                                    disabled={!visit.visit_absence_reason_id || loadingIds.has(visit.id) || actionsPaused}
                                                    title="Excluir motivo"
                                                    sx={{
                                                        color: '#d32f2f',
                                                        backgroundColor: '#ffebee',
                                                        '&:hover': {
                                                            backgroundColor: '#f8d7da',
                                                            color: '#c62828'
                                                        },
                                                        '&.Mui-disabled': {
                                                            color: '#ccc',
                                                            backgroundColor: '#f5f5f5'
                                                        },
                                                        display: visit.status === 'late' ? 'none' : 'flex'
                                                    }}
                                                >
                                                    <Delete fontSize="small" />
                                                </ActionButton>
                                            </Box>
                                        </BodyCell>
                                    </TableRow>
                                );
                            })
                        )}
                    </TableBody>
                </TableStyled>
            </TableContainerStyled>

            <PaginationWrapper>
                <Box display="flex" alignItems="center" gap={2}>
                    <PaginationInfo>
                        {paginationInfo.from} - {paginationInfo.to} de {paginationInfo.total}
                    </PaginationInfo>
                    <PerPageSelect value={rowsPerPage} onChange={handleRowsPerPageChange} variant="outlined">
                        <MenuItem value={15}>15</MenuItem>
                        <MenuItem value={25}>25</MenuItem>
                        <MenuItem value={50}>50</MenuItem>
                        <MenuItem value={100}>100</MenuItem>
                    </PerPageSelect>
                </Box>

                <Pagination count={paginationInfo.totalPages} page={page} onChange={handlePageChange} color="primary" size="small" />
            </PaginationWrapper>

            {/* Delete Confirmation Modal */}
            <Dialog open={deleteModalOpen} onClose={cancelDeleteAbsenceReason} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ color: '#3a6647', fontWeight: 'bold', fontSize: '1.4rem', pb: 2 }}>Apagar registro</DialogTitle>
                <Divider sx={{ borderColor: '#e0e0e0' }} />
                <DialogContent>
                    <Typography variant="body1" fontWeight="bold" sx={{ mb: 1, fontSize: '1.25rem' }}>
                        Deseja apagar o registro de não visita?
                    </Typography>
                    <Typography variant="body2" color="textSecondary" sx={{ fontSize: '1rem' }}>
                        Ao apagar este registro, você precisará informar novamente o motivo de não visita para esta propriedade.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button
                        onClick={cancelDeleteAbsenceReason}
                        sx={{
                            color: '#3a6647',
                            backgroundColor: 'white',
                            border: '1px solid #3a6647',
                            textTransform: 'none',
                            fontSize: '1rem',
                            '&:hover': {
                                backgroundColor: '#f8f9fa'
                            }
                        }}
                    >
                        Cancelar
                    </Button>
                    <Button
                        onClick={confirmDeleteAbsenceReason}
                        variant="contained"
                        disabled={loadingIds.has(visitToDelete?.id)}
                        sx={{
                            backgroundColor: '#3a6647',
                            color: 'white',
                            textTransform: 'none',
                            fontSize: '1rem',
                            '&:hover': {
                                backgroundColor: '#2d5038'
                            },
                            '&.Mui-disabled': {
                                backgroundColor: '#ccc',
                                color: '#999'
                            }
                        }}
                    >
                        Apagar registro
                    </Button>
                </DialogActions>
            </Dialog>

            {/* <AddVisitRuleModal
                open={isModalOpen}
                onClose={handleCloseModal}
                idEnterprise={idEnterprise}
                collaborators={collaborators}
                properties={properties}
                onSuccess={handleModalSuccess}
            /> */}
        </Box>
    );
};

VisitsAbsencesTable.propTypes = {
    onDataChange: PropTypes.func,
    idEnterprise: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    absenceReasons: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.number,
            name: PropTypes.string
        })
    ),
    filterPer: PropTypes.shape({
        collaborators: PropTypes.arrayOf(
            PropTypes.shape({
                id: PropTypes.number,
                name: PropTypes.string
            })
        ),
        properties: PropTypes.arrayOf(
            PropTypes.shape({
                id: PropTypes.number,
                name: PropTypes.string
            })
        ),
        period: PropTypes.oneOf(['Meia', 'Inteira'])
    }),
    collaborators: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.number,
            name: PropTypes.string
        })
    ),
    properties: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.number,
            name: PropTypes.string
        })
    )
};
